import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Download,
  BookOpen,
  ChevronDown,
  Sparkles,
  ArrowRight,
  Quote,
  CheckCircle2,
  Calendar,
  Users,
  Heart,
  GraduationCap,
  Compass,
  ListChecks,
  Feather,
  Footprints,
  Info,
} from 'lucide-react'
import {
  manual,
  welcome,
  howItWorks,
  beforeYouBegin,
  overview,
  parts,
  conclusion,
} from '../../data/discipleship'

const themeColors = {
  'The Problem': 'text-rose-600 bg-rose-50',
  'The Solution': 'text-primary-600 bg-primary-50',
  'The Commission': 'text-secondary-600 bg-secondary-50',
  'The Challenge': 'text-accent-600 bg-accent-50',
  'The Transformation': 'text-emerald-600 bg-emerald-50',
  'The Commitment': 'text-indigo-600 bg-indigo-50',
}

/* -------------------------------------------------------------------------- */
/*  Small building blocks                                                      */
/* -------------------------------------------------------------------------- */

const DownloadButton = ({ className = '', size = 'md' }) => (
  <motion.a
    href={manual.pdfUrl}
    download="Discipleship-Training-Manual.pdf"
    target="_blank"
    rel="noopener noreferrer"
    whileHover={{ scale: 1.04, y: -2 }}
    whileTap={{ scale: 0.97 }}
    className={`inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-accent-500 to-accent-600 text-white font-accent font-bold shadow-lg shadow-accent-500/30 ${
      size === 'lg' ? 'px-8 py-4 text-lg' : 'px-6 py-3.5'
    } ${className}`}
  >
    <Download className={size === 'lg' ? 'w-6 h-6' : 'w-5 h-5'} />
    Download PDF
  </motion.a>
)

const VerseBlock = ({ text, reference }) => (
  <blockquote className="relative my-6 rounded-2xl border-l-4 border-primary-400 bg-gradient-to-br from-primary-50 via-secondary-50/40 to-white p-6 pl-12">
    <Quote className="absolute left-4 top-5 w-5 h-5 text-primary-400" />
    <p className="text-dark-100 italic leading-relaxed text-[15px] md:text-base">{text}</p>
    <cite className="mt-3 block not-italic font-accent font-bold text-primary-600 text-sm">
      — {reference}
    </cite>
  </blockquote>
)

const ContentTable = ({ headers, rows }) => (
  <div className="my-6 overflow-x-auto rounded-2xl border border-gray-100 shadow-soft">
    <table className="w-full text-left text-sm">
      <thead>
        <tr className="bg-gradient-to-r from-dark-100 to-dark-200 text-white">
          {headers.map((h, i) => (
            <th key={i} className="px-4 py-3 font-accent font-semibold whitespace-nowrap">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={ri} className={ri % 2 ? 'bg-light-100' : 'bg-white'}>
            {row.map((cell, ci) => (
              <td
                key={ci}
                className={`px-4 py-3 align-top text-dark-300 ${ci === 0 ? 'font-semibold text-dark-100' : ''}`}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

const LessonBlock = ({ block }) => {
  switch (block.type) {
    case 'text':
      return (
        <p
          className="text-dark-300 leading-relaxed mb-4"
          dangerouslySetInnerHTML={{ __html: block.html }}
        />
      )
    case 'verse':
      return <VerseBlock text={block.text} reference={block.ref} />
    case 'subheading':
      return (
        <h4 className="font-heading font-bold text-dark-100 text-lg mt-6 mb-2">{block.text}</h4>
      )
    case 'question':
      return (
        <div className="flex gap-3 mb-4">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-100 text-primary-700 font-bold text-sm flex items-center justify-center">
            {block.n}
          </span>
          <p className="text-dark-300 leading-relaxed pt-0.5">{block.text}</p>
        </div>
      )
    case 'note':
      return (
        <div className="my-5 rounded-2xl bg-amber-50 border border-amber-100 p-5">
          <p className="text-amber-900/90 text-[15px] leading-relaxed">{block.text}</p>
        </div>
      )
    case 'table':
      return <ContentTable headers={block.headers} rows={block.rows} />
    default:
      return null
  }
}

const LessonCard = ({ lesson, gradient, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen)

  return (
    <div className="rounded-3xl bg-white border border-gray-100 shadow-soft overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-4 p-5 md:p-6 text-left hover:bg-light-100/60 transition-colors"
      >
        <span
          className={`flex-shrink-0 w-14 h-14 rounded-2xl bg-gradient-to-br ${gradient} text-white flex flex-col items-center justify-center shadow-lg leading-none`}
        >
          <span className="text-[10px] font-accent uppercase tracking-wide opacity-90">Wk</span>
          <span className="text-xl font-heading font-bold">{lesson.week}</span>
        </span>
        <div className="flex-grow min-w-0">
          <div className="flex items-center gap-2 text-xs text-dark-300 mb-1">
            <Calendar className="w-3.5 h-3.5" />
            <span className="font-medium">{lesson.dates}</span>
          </div>
          <h3 className="font-heading font-bold text-dark-100 text-lg md:text-xl truncate">
            {lesson.title}
          </h3>
        </div>
        <motion.span
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="flex-shrink-0 text-dark-300"
        >
          <ChevronDown className="w-6 h-6" />
        </motion.span>
      </button>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="px-5 md:px-8 pb-8 pt-2 border-t border-gray-100">
              {lesson.blocks.map((block, i) => (
                <LessonBlock key={i} block={block} />
              ))}

              {/* Time to Reflect */}
              {lesson.reflect && (
                <div className="mt-6 rounded-2xl bg-gradient-to-br from-secondary-50 to-primary-50 border border-secondary-100 p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ListChecks className="w-5 h-5 text-secondary-600" />
                    <h4 className="font-accent font-bold text-secondary-700 uppercase text-sm tracking-wide">
                      Time to Reflect
                    </h4>
                  </div>
                  {lesson.reflect.prompt ? (
                    <p className="text-dark-300 leading-relaxed">{lesson.reflect.prompt}</p>
                  ) : (
                    <ul className="space-y-2.5">
                      {lesson.reflect.options.map((opt, i) => (
                        <li key={i} className="flex items-start gap-3 text-dark-300">
                          <CheckCircle2 className="w-5 h-5 text-secondary-400 flex-shrink-0 mt-0.5" />
                          <span>{opt}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Review */}
              {lesson.review && (
                <div className="mt-4 rounded-2xl bg-dark-100 text-white p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles className="w-5 h-5 text-accent-400" />
                    <h4 className="font-accent font-bold uppercase text-sm tracking-wide text-accent-300">
                      Review
                    </h4>
                  </div>
                  <p className="text-white/80 leading-relaxed">{lesson.review}</p>
                </div>
              )}

              {/* Next Steps (final week) */}
              {lesson.nextSteps && (
                <div className="mt-4 rounded-2xl bg-gradient-to-r from-accent-500 to-accent-600 text-white p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Footprints className="w-5 h-5" />
                    <h4 className="font-accent font-bold uppercase text-sm tracking-wide">
                      Next Steps
                    </h4>
                  </div>
                  <p className="text-white/90 leading-relaxed">{lesson.nextSteps}</p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

const InterludeCard = ({ interlude }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="rounded-3xl bg-gradient-to-br from-dark-100 to-dark-200 text-white p-6 md:p-8 shadow-soft-lg"
  >
    <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs font-accent font-semibold uppercase tracking-wide mb-4">
      <Info className="w-3.5 h-3.5 text-accent-400" />
      Just So You Know…
    </span>
    <h3 className="font-heading font-bold text-2xl mb-4">{interlude.title}</h3>
    {interlude.paragraphs?.map((p, i) => (
      <p
        key={i}
        className="text-white/75 leading-relaxed mb-3 [&_strong]:text-white [&_strong]:font-semibold"
        dangerouslySetInnerHTML={{ __html: p }}
      />
    ))}
    {interlude.table && (
      <div className="mt-4 overflow-x-auto rounded-2xl border border-white/10">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="bg-white/5">
              {interlude.table.headers.map((h, i) => (
                <th key={i} className="px-4 py-3 font-accent font-semibold text-white whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {interlude.table.rows.map((row, ri) => (
              <tr key={ri} className="border-t border-white/10">
                {row.map((cell, ci) => (
                  <td
                    key={ci}
                    className={`px-4 py-3 align-top ${ci === 0 ? 'font-semibold text-white' : 'text-white/70'}`}
                  >
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </motion.div>
)

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

const Discipleship = () => {
  useEffect(() => {
    const original = document.title
    document.title = `${manual.seriesTitle}: Discipleship Training Manual | DLBC Clarksville`

    // Keep this unlisted page out of search results (shared privately with members).
    const robotsMeta = document.createElement('meta')
    robotsMeta.name = 'robots'
    robotsMeta.content = 'noindex, nofollow'
    document.head.appendChild(robotsMeta)

    return () => {
      document.title = original
      document.head.removeChild(robotsMeta)
    }
  }, [])

  return (
    <>
      {/* ------------------------------------------------------------------ */}
      {/* Hero                                                                */}
      {/* ------------------------------------------------------------------ */}
      <section className="relative overflow-hidden pt-20 pb-24 md:pt-28 md:pb-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-dark-100 to-dark" />
          <div className="absolute top-1/4 right-1/4 w-[500px] h-[500px] bg-primary-500/20 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 left-1/4 w-[500px] h-[500px] bg-secondary-500/20 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/20">
                <GraduationCap className="w-4 h-4 text-accent-400" />
                {manual.kicker}
              </span>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-heading font-bold text-white leading-tight mb-3">
                {manual.seriesTitle}
              </h1>
              <p className="text-2xl md:text-3xl font-heading font-semibold bg-gradient-to-r from-accent-400 to-orange-400 bg-clip-text text-transparent mb-6">
                {manual.seriesSubtitle}
              </p>
              <p className="text-lg text-white/70 max-w-xl mb-8">
                An eighteen-week journey from believer to disciple — one lesson a week, walked
                alongside a Small Group Leader. Read it here, or download the workbook to your phone
                or tablet.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <DownloadButton size="lg" />
                <motion.a
                  href="#study"
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 text-white font-accent font-bold text-lg hover:bg-white/20 transition-colors"
                >
                  <BookOpen className="w-6 h-6" />
                  Start Reading
                </motion.a>
              </div>
              <p className="mt-6 text-white/50 text-sm font-accent tracking-wide">
                {manual.church} · {manual.studyLabel}
              </p>
            </motion.div>

            {/* Cover */}
            <motion.div
              initial={{ opacity: 0, scale: 0.92, rotate: 2 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.7, delay: 0.15 }}
              className="hidden lg:flex justify-center"
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-primary-500/30 to-secondary-500/30 rounded-[2rem] blur-2xl" />
                <img
                  src={manual.coverUrl}
                  alt={`${manual.seriesTitle}: ${manual.seriesSubtitle}`}
                  className="relative w-full max-w-sm rounded-3xl shadow-2xl ring-1 ring-white/20"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Welcome                                                             */}
      {/* ------------------------------------------------------------------ */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-light-100 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4">
              <Heart className="w-4 h-4" fill="currentColor" />
              Welcome
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mb-6">
              {welcome.heading}
            </h2>
            {welcome.paragraphs.map((p, i) => (
              <p key={i} className="text-lg text-dark-300 leading-relaxed mb-4">
                {p}
              </p>
            ))}
            <VerseBlock text={welcome.verse.text} reference={welcome.verse.ref} />
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Download banner                                                     */}
      {/* ------------------------------------------------------------------ */}
      <section className="pb-16 md:pb-20 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary-600 via-secondary-600 to-primary-600 bg-[length:200%_100%] animate-gradient p-8 md:p-10"
          >
            <div className="absolute -top-10 -right-10 w-48 h-48 bg-white/10 rounded-full blur-2xl" />
            <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
              <div className="flex items-center gap-5">
                <div className="hidden sm:flex w-16 h-16 rounded-2xl bg-white/15 items-center justify-center flex-shrink-0">
                  <Download className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-heading font-bold text-white mb-1">
                    Take it with you
                  </h3>
                  <p className="text-white/80 max-w-md">
                    Download the full write-in workbook as a PDF to read, print, and complete on your
                    phone, tablet, or computer.
                  </p>
                </div>
              </div>
              <DownloadButton className="!bg-white !text-primary-700 !shadow-white/20 flex-shrink-0" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* How this study works                                                */}
      {/* ------------------------------------------------------------------ */}
      <section className="py-16 md:py-24 bg-light-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-100 text-secondary-700 text-sm font-semibold mb-4">
              <Compass className="w-4 h-4" />
              How This Study Works
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mb-4">
              A Guide, Not Just a Book
            </h2>
            <p className="text-lg text-dark-300 max-w-2xl mx-auto">{howItWorks.intro}</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6 mb-10">
            {howItWorks.columns.map((col, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-3xl bg-white p-6 shadow-soft border border-gray-100"
              >
                <h3 className="font-heading font-bold text-dark-100 text-lg mb-1">{col.title}</h3>
                {col.note && <p className="text-xs text-dark-300 mb-4">{col.note}</p>}
                <ul className="space-y-2.5 mt-3">
                  {col.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-2.5 text-dark-300 text-[15px]">
                      <CheckCircle2 className="w-4 h-4 text-primary-500 flex-shrink-0 mt-1" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>

          {/* Success factors */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl bg-gradient-to-br from-dark-100 to-dark-200 p-8 md:p-10 text-white"
          >
            <h3 className="font-heading font-bold text-xl mb-6 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-accent-400" />
              Your success depends on…
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {howItWorks.successFactors.map((f, i) => (
                <div key={i}>
                  <p className="font-heading font-bold text-accent-300 text-lg mb-1">{f.label}</p>
                  <p className="text-white/70 text-sm leading-relaxed">{f.text}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 pt-6 border-t border-white/10 grid sm:grid-cols-2 gap-6">
              {howItWorks.sectionsToNote.map((s, i) => (
                <p key={i} className="text-white/70 text-sm">
                  <span className="font-semibold text-accent-300">{s.label}</span> — {s.text}
                </p>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Before you begin                                                    */}
      {/* ------------------------------------------------------------------ */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-10"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-100 text-accent-700 text-sm font-semibold mb-4">
              <Feather className="w-4 h-4" />
              Before You Begin
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100">
              {beforeYouBegin.heading}
            </h2>
          </motion.div>

          <div className="space-y-6">
            {beforeYouBegin.stories.map((story, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="rounded-3xl bg-light-100 p-6 md:p-8 border border-gray-100"
              >
                <h3 className="font-heading font-bold text-xl text-primary-700 mb-4">
                  {story.title}
                </h3>
                {story.paragraphs.map((p, j) => (
                  <p key={j} className="text-dark-300 leading-relaxed mb-3 last:mb-0">
                    {p}
                  </p>
                ))}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Schedule overview                                                   */}
      {/* ------------------------------------------------------------------ */}
      <section id="study" className="py-16 md:py-24 bg-light-100 scroll-mt-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-100 text-primary-700 text-sm font-semibold mb-4">
              <Calendar className="w-4 h-4" />
              The Journey
            </span>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100 mb-4">
              August – December Overview
            </h2>
            <p className="text-lg text-dark-300 max-w-2xl mx-auto">
              Eighteen weekly lessons, one Small Group Leader meeting each week. The study runs
              straight through, including Thanksgiving week.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="overflow-x-auto rounded-3xl bg-white shadow-soft border border-gray-100"
          >
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="bg-gradient-to-r from-dark-100 to-dark-200 text-white">
                  <th className="px-4 py-4 font-accent font-semibold">Week</th>
                  <th className="px-4 py-4 font-accent font-semibold">Dates</th>
                  <th className="px-4 py-4 font-accent font-semibold">Theme</th>
                  <th className="px-4 py-4 font-accent font-semibold">Lesson</th>
                </tr>
              </thead>
              <tbody>
                {overview.map((row, i) => (
                  <tr key={row.week} className={`${i % 2 ? 'bg-light-100/60' : 'bg-white'} border-t border-gray-100`}>
                    <td className="px-4 py-3 font-bold text-dark-100">{row.week}</td>
                    <td className="px-4 py-3 text-dark-300 whitespace-nowrap">{row.dates}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${themeColors[row.theme]}`}>
                        {row.theme}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-dark-100 font-medium">{row.lesson}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Parts & lessons                                                     */}
      {/* ------------------------------------------------------------------ */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Quick jump */}
          <div className="mb-14 rounded-3xl bg-light-100 border border-gray-100 p-6">
            <p className="font-accent font-semibold text-dark-300 text-sm uppercase tracking-wide mb-4 flex items-center gap-2">
              <ListChecks className="w-4 h-4 text-primary-500" />
              Jump to a section
            </p>
            <div className="flex flex-wrap gap-2">
              {parts.map((part) => (
                <a
                  key={part.id}
                  href={`#${part.id}`}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-gray-200 text-sm font-medium text-dark-300 hover:border-primary-300 hover:text-primary-600 transition-colors"
                >
                  <span className={`w-2.5 h-2.5 rounded-full bg-gradient-to-br ${part.gradient}`} />
                  {part.name}
                </a>
              ))}
            </div>
          </div>

          <div className="space-y-16">
            {parts.map((part, pi) => (
              <div key={part.id} id={part.id} className="scroll-mt-24">
                {/* Part header */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  className="mb-6"
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${part.gradient} text-white font-heading font-bold shadow-lg`}>
                      {pi + 1}
                    </span>
                    <span className="font-accent font-semibold text-dark-300 uppercase text-xs tracking-widest">
                      Part {pi + 1}
                    </span>
                  </div>
                  <h2 className="text-3xl md:text-4xl font-heading font-bold text-dark-100">
                    {part.name}
                  </h2>
                  <p className="text-lg text-dark-300 mt-1">{part.question}</p>
                </motion.div>

                {/* Interlude */}
                {part.interlude && (
                  <div className="mb-6">
                    <InterludeCard interlude={part.interlude} />
                  </div>
                )}

                {/* Lessons */}
                <div className="space-y-4">
                  {part.lessons.map((lesson) => (
                    <LessonCard
                      key={lesson.week}
                      lesson={lesson}
                      gradient={part.gradient}
                      defaultOpen={pi === 0 && lesson.week === 1}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Conclusion                                                          */}
      {/* ------------------------------------------------------------------ */}
      <section className="py-20 md:py-28 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary-500 via-secondary-500 to-accent-500" />
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm text-white text-sm font-semibold mb-6 border border-white/30">
              <Footprints className="w-4 h-4" />
              The Finish Line
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
              {conclusion.heading}
            </h2>
            {conclusion.paragraphs.map((p, i) => (
              <p key={i} className="text-lg text-white/85 leading-relaxed mb-4">
                {p}
              </p>
            ))}
            <p className="text-xl font-heading font-semibold text-white mt-6 mb-10">
              {conclusion.charge}
            </p>

            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <DownloadButton className="!bg-white !text-primary-700" size="lg" />
              <Link to="/contact">
                <motion.button
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-2xl border-2 border-white text-white font-accent font-bold text-lg hover:bg-white/10 transition-colors"
                >
                  <Users className="w-6 h-6" />
                  Join a Small Group
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ------------------------------------------------------------------ */}
      {/* Related next steps                                                  */}
      {/* ------------------------------------------------------------------ */}
      <section className="py-16 md:py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { to: '/new-here', icon: Heart, label: "I'm New Here", desc: 'Start your journey with Christ' },
              { to: '/ministries', icon: Users, label: 'Find a Group', desc: 'Grow alongside others' },
              { to: '/plan-your-visit', icon: ArrowRight, label: 'Plan a Visit', desc: 'Worship with us Sunday' },
            ].map((c) => (
              <Link
                key={c.to}
                to={c.to}
                className="group rounded-3xl bg-light-100 hover:bg-white border border-gray-100 hover:border-primary-200 hover:shadow-soft-lg p-6 transition-all"
              >
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <c.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="font-heading font-bold text-dark-100 mb-1">{c.label}</h3>
                <p className="text-dark-300 text-sm">{c.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}

export default Discipleship
